import React, { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Archive,
  ArchiveRestore,
  CalendarX,
  CheckCircle2,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  XCircle,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { parseEventIdFromReminderActionUrl } from "@/features/breakfast/lib/eventReminderNotif";
import { cn } from "@/lib/utils";
import { relativeTime } from "../lib/format";
import type { DBNotification } from "../types";
import type { SenderProfile } from "../hooks/useNotifSenders";
import { NotifAvatar } from "./NotifAvatar";

interface NotifRowProps {
  notif: DBNotification;
  compact: boolean;
  inHistory: boolean;
  /**
   * Resolved profile for `notif.sender_id`:
   *   • `SenderProfile` — resolved successfully.
   *   • `null`          — sender expected but no profile exists (deleted
   *                       account). `NotifAvatar` renders the "Former
   *                       member" placeholder and we still render the
   *                       row — we never throw for orphaned senders.
   *   • `undefined`     — no sender or not yet resolved.
   */
  sender?: SenderProfile | null;
  /**
   * When true, this row is the target of a deep-link from the popover.
   * Scrolls itself into view and renders a pulsing ring for ~1.8s so
   * the user can visually latch onto it after the sheet animates open.
   */
  highlighted?: boolean;
  /**
   * Fires when the row surface (outside of inline action buttons) is
   * clicked in the popover's compact mode. The bell uses this to open
   * the full Notification Centre focused on this row.
   */
  onRowClick?: (id: string) => void;
  checked?: boolean;
  onCheck?: (id: string) => void;
  onMarkRead: (id: string) => void;
  onArchive: (id: string) => void;
  onDelete: (id: string) => void;
  onUnarchive: (id: string) => void;
  onAcceptInvite?: (notif: DBNotification) => void;
  onDeclineInvite?: (notif: DBNotification) => void;
  onAcceptBuddy?: (notif: DBNotification) => void;
  onDeclineBuddy?: (notif: DBNotification) => void;
  onAcceptBreakfast?: (notif: DBNotification) => void;
  onDeclineBreakfast?: (notif: DBNotification) => void;
  /**
   * Fires when the user taps "Remove from calendar" on an
   * `EVENT_CANCELLED` row. Only rendered when both the notif is of type
   * `EVENT_CANCELLED` AND this callback is provided. See
   * `useEventCancellationActions` for the RPC this wraps.
   */
  onRemoveFromCalendar?: (notif: DBNotification) => void;
  /**
   * Fires when the user taps "Confirm" on a `DAILY_REMINDER` row.
   */
  onConfirmAttendance?: (notif: DBNotification) => void;
  /**
   * Fires when the user taps "Clear" on a `DAILY_REMINDER` row.
   */
  onClearAttendance?: (notif: DBNotification) => void;
}

/**
 * Renders a single DB-backed notification row — used inside both the
 * popover preview (in compact mode) and the full notification sheet.
 *
 * Branches on `notif.type` to decide which inline Accept/Decline buttons
 * to show:
 *
 *   • workspace_invite  → workspace membership flow (Accept / Decline).
 *   • BUDDY_INVITE      → circle invite flow (Accept / Decline).
 *   • BREAKFAST_INVITE  → fallback RSVP for events not already covered by
 *     + EVENT_UPDATE      a first-class `BreakfastInviteCard`.
 *
 * `EVENT_UPDATE` (reschedule reset) is treated identically to
 * `BREAKFAST_INVITE` at the action layer — both demand an RSVP decision
 * — but renders an amber "Rescheduled" chip next to the title so the
 * user understands the trigger.
 *
 * Hover-reveal actions (Mark read / Archive / Delete / Restore) only
 * render in non-compact mode. The compact popover surfaces them one tap
 * away in the full sheet.
 */
export function NotifRow({
  notif,
  compact,
  inHistory,
  sender,
  highlighted,
  onRowClick,
  checked,
  onCheck,
  onMarkRead,
  onArchive,
  onDelete,
  onUnarchive,
  onAcceptInvite,
  onDeclineInvite,
  onAcceptBuddy,
  onDeclineBuddy,
  onAcceptBreakfast,
  onDeclineBreakfast,
  onRemoveFromCalendar,
  onConfirmAttendance,
  onClearAttendance,
}: NotifRowProps) {
  const isWorkspaceInvite = notif.type === "workspace_invite" && !inHistory;
  const isBuddyInvite = notif.type === "BUDDY_INVITE" && !inHistory;
  const isEventCancelled = notif.type === "EVENT_CANCELLED" && !inHistory;
  const isEventReminder  = notif.type === "EVENT_REMINDER" && !inHistory;
  const isDailyReminder  = (
    notif.type === "DAILY_REMINDER" || 
    notif.type === "attendance" || 
    notif.title.toLowerCase().includes("daily")
  ) && !inHistory;
  const reminderEventId = useMemo(
    () =>
      isEventReminder
        ? parseEventIdFromReminderActionUrl(notif.action_url)
        : null,
    [isEventReminder, notif.action_url],
  );

  // Scroll the row into view + fire the highlight ring animation when
  // the user deep-links into the sheet from the popover. `block: "center"`
  // keeps the row comfortably away from the sheet header.
  const rootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!highlighted) return;
    rootRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlighted]);

  // Treat BREAKFAST_INVITE (new invite) and EVENT_UPDATE (reschedule
  // reset) identically at the action layer: both demand an RSVP decision
  // from the guest. The BreakfastInviteCard at the top of the sheet
  // dedupes the common case; anything reaching this fallback row is a
  // notification whose matching `breakfast_attendees` pending row has
  // already been consumed — Accept/Decline here route through the legacy
  // `handle_breakfast_rsvp` RPC path.
  const isBreakfastInvite =
    (notif.type === "BREAKFAST_INVITE" || notif.type === "EVENT_UPDATE") &&
    !inHistory;
  const isRescheduleNotice = notif.type === "EVENT_UPDATE";

  const isClickable = compact && !!onRowClick;

  return (
    <>
      {/* Scoped highlight keyframes — unique class names keep them from
          bleeding into any other row's animation state. */}
      <style>{`
        @keyframes notif-highlight-ring {
          0%   { box-shadow: 0 0 0 0 hsl(var(--primary) / 0.55); background-color: hsl(var(--primary) / 0.08); }
          60%  { box-shadow: 0 0 0 6px hsl(var(--primary) / 0);    background-color: hsl(var(--primary) / 0.08); }
          100% { box-shadow: 0 0 0 0 hsl(var(--primary) / 0);      background-color: transparent; }
        }
        .notif-row-highlighted { animation: notif-highlight-ring 1.8s ease-out 1; }
      `}</style>
    {/* Nesting: rows contain Accept/Decline/Link; an outer <button> would be invalid. */}
    {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
    <div
      ref={rootRef}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={isClickable ? () => onRowClick!(notif.id) : undefined}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onRowClick!(notif.id);
              }
            }
          : undefined
      }
      className={cn(
        "flex items-start gap-3 group transition-colors hover:bg-muted/30 rounded-md",
        compact ? "px-4 py-3" : "px-5 py-4",
        !notif.is_read && !inHistory && "bg-primary/3",
        isClickable && "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        highlighted && "notif-row-highlighted",
      )}
    >
      {/* Checkbox (History only) */}
      {inHistory && onCheck && (
        <Checkbox
          checked={checked}
          onCheckedChange={() => onCheck(notif.id)}
          className="mt-1 flex-shrink-0"
        />
      )}

      {/* Unread dot (Inbox only) */}
      {!inHistory && !notif.is_read && (
        <span className="h-2 w-2 rounded-full bg-primary flex-shrink-0 mt-2" />
      )}
      {!inHistory && notif.is_read && (
        <span className="h-2 w-2 flex-shrink-0 mt-2" />
      )}

      {/* Leading avatar: real sender photo when available, themed
          initials chip as a first fallback, themed Lucide icon for
          system alerts (no sender_id). */}
      <div className="mt-0.5">
        <NotifAvatar type={notif.type} sender={sender} compact={compact} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p
            className={cn(
              "font-medium leading-tight flex items-center gap-1.5 flex-wrap",
              compact ? "text-xs" : "text-sm",
              notif.is_read && "font-normal text-muted-foreground",
            )}
          >
            <span className="truncate">{notif.title}</span>
            {isRescheduleNotice && !inHistory && (
              <span
                className={cn(
                  "shrink-0 rounded-full font-bold uppercase tracking-wide",
                  "bg-amber-500/15 text-amber-600 dark:text-amber-400",
                  compact
                    ? "text-[9px] px-1.5 py-[1px]"
                    : "text-[10px] px-2 py-0.5",
                )}
              >
                Rescheduled
              </span>
            )}
          </p>
          <span className="text-[10px] text-muted-foreground whitespace-nowrap flex-shrink-0">
            {relativeTime(notif.created_at)}
          </span>
        </div>
        {notif.message && (
          <p
            className={cn(
              "text-muted-foreground mt-0.5 leading-relaxed line-clamp-2",
              compact ? "text-[10px]" : "text-xs",
            )}
          >
            {notif.message}
          </p>
        )}

        {/* Workspace invite: Accept / Decline */}
        {isWorkspaceInvite && (onAcceptInvite || onDeclineInvite) && (
          <div className={cn("flex gap-1.5 mt-2.5", compact && "mt-2")}>
            {onAcceptInvite && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAcceptInvite(notif);
                }}
                className={cn(
                  "flex items-center gap-1 font-medium rounded-md",
                  "bg-primary text-primary-foreground hover:bg-primary/90 transition-colors",
                  compact ? "text-[10px] px-2 py-0.5" : "text-xs px-2.5 py-1",
                )}
              >
                <ThumbsUp className="h-3 w-3" />
                Accept
              </button>
            )}
            {onDeclineInvite && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDeclineInvite(notif);
                }}
                className={cn(
                  "flex items-center gap-1 font-medium rounded-md",
                  "bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors",
                  compact ? "text-[10px] px-2 py-0.5" : "text-xs px-2.5 py-1",
                )}
              >
                <ThumbsDown className="h-3 w-3" />
                Decline
              </button>
            )}
          </div>
        )}

        {/* Breakfast invite: Accept / Decline (text-only, no icons) */}
        {isBreakfastInvite && (onAcceptBreakfast || onDeclineBreakfast) && (
          <div className={cn("flex gap-1.5 mt-2.5", compact && "mt-2")}>
            {onAcceptBreakfast && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAcceptBreakfast(notif);
                }}
                className={cn(
                  "font-semibold rounded-md transition-colors",
                  "bg-primary text-primary-foreground hover:bg-primary/90",
                  compact ? "text-[10px] px-2 py-0.5" : "text-xs px-2.5 py-1",
                )}
              >
                Accept
              </button>
            )}
            {onDeclineBreakfast && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDeclineBreakfast(notif);
                }}
                className={cn(
                  "font-medium rounded-md transition-colors",
                  "bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80",
                  compact ? "text-[10px] px-2 py-0.5" : "text-xs px-2.5 py-1",
                )}
              >
                Decline
              </button>
            )}
          </div>
        )}

        {/* Event cancelled: single "Remove from calendar" action.
            Mirrors Outlook's "Remove from calendar" prompt — this is the
            ONLY user path that hard-deletes the attendee row from the
            viewer's own calendar and clears the bell notification.
            Tapping it archives this row and invalidates the breakfast
            caches via `useEventCancellationActions`. */}
        {isEventCancelled && onRemoveFromCalendar && (
          <div className={cn("flex gap-1.5 mt-2.5", compact && "mt-2")}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRemoveFromCalendar(notif);
              }}
              className={cn(
                "flex items-center gap-1 font-medium rounded-md transition-colors",
                "bg-destructive/10 text-destructive hover:bg-destructive/15 hover:text-destructive",
                compact ? "text-[10px] px-2 py-0.5" : "text-xs px-2.5 py-1",
              )}
            >
              <CalendarX className="h-3 w-3" />
              Remove from calendar
            </button>
          </div>
        )}

        {isEventReminder && reminderEventId && (
          <div className={cn("mt-2.5", compact && "mt-2")}>
            <Link
              to={`/circles?eventId=${encodeURIComponent(reminderEventId)}`}
              onClick={(e) => e.stopPropagation()}
              className={cn(
                "inline-flex font-medium rounded-md transition-colors text-primary hover:underline",
                compact ? "text-[10px]" : "text-xs",
              )}
            >
              Ver evento
            </Link>
          </div>
        )}

        {/* Daily reminder: Confirm / Clear */}
        {isDailyReminder && (onConfirmAttendance || onClearAttendance) && (
          <div className={cn("flex gap-1.5 mt-2.5", compact && "mt-2")}>
            {onConfirmAttendance && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onConfirmAttendance(notif);
                }}
                className={cn(
                  "flex items-center gap-1 font-medium rounded-md",
                  "bg-violet-500 text-white hover:bg-violet-600 transition-colors",
                  compact ? "text-[10px] px-2 py-0.5" : "text-xs px-2.5 py-1",
                )}
              >
                <CheckCircle2 className="h-3 w-3" />
                Confirm
              </button>
            )}
            {onClearAttendance && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClearAttendance(notif);
                }}
                className={cn(
                  "flex items-center gap-1 font-medium rounded-md",
                  "bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors",
                  compact ? "text-[10px] px-2 py-0.5" : "text-xs px-2.5 py-1",
                )}
              >
                <XCircle className="h-3 w-3" />
                Clear
              </button>
            )}
          </div>
        )}

        {/* Circle invite: Accept / Decline */}
        {isBuddyInvite && (onAcceptBuddy || onDeclineBuddy) && (
          <div className={cn("flex gap-1.5 mt-2.5", compact && "mt-2")}>
            {onAcceptBuddy && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAcceptBuddy(notif);
                }}
                className={cn(
                  "flex items-center gap-1 font-medium rounded-md",
                  "bg-violet-500 text-white hover:bg-violet-600 transition-colors",
                  compact ? "text-[10px] px-2 py-0.5" : "text-xs px-2.5 py-1",
                )}
              >
                <CheckCircle2 className="h-3 w-3" />
                Accept
              </button>
            )}
            {onDeclineBuddy && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDeclineBuddy(notif);
                }}
                className={cn(
                  "flex items-center gap-1 font-medium rounded-md",
                  "bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors",
                  compact ? "text-[10px] px-2 py-0.5" : "text-xs px-2.5 py-1",
                )}
              >
                <XCircle className="h-3 w-3" />
                Decline
              </button>
            )}
          </div>
        )}

        {/* Action buttons — visible on hover in full view */}
        {!compact && (
          <div className="flex gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
            {inHistory ? (
              <>
                <button
                  onClick={() => onUnarchive(notif.id)}
                  className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors px-1.5 py-0.5 rounded hover:bg-muted"
                >
                  <ArchiveRestore className="h-3 w-3" />
                  Restore
                </button>
                <button
                  onClick={() => onDelete(notif.id)}
                  className="flex items-center gap-1 text-[10px] text-destructive hover:text-destructive/80 transition-colors px-1.5 py-0.5 rounded hover:bg-destructive/10"
                >
                  <Trash2 className="h-3 w-3" />
                  Delete
                </button>
              </>
            ) : (
              <>
                {!notif.is_read && (
                  <button
                    onClick={() => onMarkRead(notif.id)}
                    className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors px-1.5 py-0.5 rounded hover:bg-muted"
                  >
                    <CheckCircle2 className="h-3 w-3" />
                    Mark read
                  </button>
                )}
                <button
                  onClick={() => onArchive(notif.id)}
                  className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors px-1.5 py-0.5 rounded hover:bg-muted"
                >
                  <Archive className="h-3 w-3" />
                  Archive
                </button>
                <button
                  onClick={() => onDelete(notif.id)}
                  className="flex items-center gap-1 text-[10px] text-destructive hover:text-destructive/80 transition-colors px-1.5 py-0.5 rounded hover:bg-destructive/10"
                >
                  <Trash2 className="h-3 w-3" />
                  Delete
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
    </>
  );
}
